/**
 * Extension allowlists aligned with Adobe Premiere's supported import formats.
 * @see https://helpx.adobe.com/premiere-pro/using/supported-file-formats.html
 *
 * Transcript import from the asset review panel uses a separate host path
 * (`importTranscript`); SRT is included here so standalone `.srt` clips on
 * boards can be batch-imported via `importFiles`.
 */

import { ClipType } from '@air/api/types';

const normalizeExtension = (ext: string): string => ext.trim().toLowerCase().replace(/^\./, '');

/** Supported still-image and still-image sequence formats. */
export const PREMIERE_STILL_IMAGE_IMPORT_EXTENSIONS = [
  'ai',
  'bmp',
  'dib',
  'dpx',
  'eps',
  'gif',
  'heic',
  'heif',
  'icb',
  'ico',
  'jfif',
  'jpe',
  'jpeg',
  'jpg',
  'pct',
  'pict',
  'png',
  'prtl',
  'psd',
  'ptl',
  'rle',
  'tga',
  'tif',
  'tiff',
  'vda',
  'vst',
] as const;

/** Supported native video and audio formats (by common file extension). */
export const PREMIERE_MEDIA_IMPORT_EXTENSIONS = [
  '3g2',
  '3gp',
  'aac',
  'aif',
  'aiff',
  'asf',
  'asnd',
  'avi',
  'bwf',
  'crm',
  'exr',
  'm1v',
  'm2t',
  'm2ts',
  'm2v',
  'm4a',
  'm4v',
  'mov',
  'mp3',
  'mp4',
  'mpe',
  'mpeg',
  'mpg',
  'mts',
  'mxr',
  'mxf',
  'r3d',
  'sxr',
  'vob',
  'wav',
  'wmv',
] as const;

/** Supported closed-caption and subtitle formats. */
export const PREMIERE_CAPTION_IMPORT_EXTENSIONS = ['dfxp', 'mcc', 'scc', 'srt', 'stl'] as const;

/** Motion Graphics Templates — installed into Premiere My Templates, not importFiles. */
export const PREMIERE_GRAPHICS_IMPORT_EXTENSIONS = ['mogrt'] as const;

export const PREMIERE_CLIP_IMPORT_EXTENSIONS = [
  ...PREMIERE_STILL_IMAGE_IMPORT_EXTENSIONS,
  ...PREMIERE_MEDIA_IMPORT_EXTENSIONS,
  ...PREMIERE_CAPTION_IMPORT_EXTENSIONS,
  ...PREMIERE_GRAPHICS_IMPORT_EXTENSIONS,
] as const;

const PREMIERE_CLIP_IMPORT_EXTENSION_SET = new Set<string>(PREMIERE_CLIP_IMPORT_EXTENSIONS.map(normalizeExtension));

export const isPremiereImportableExtension = (ext: string | undefined | null): boolean => {
  if (ext == null || ext.trim() === '') {
    return false;
  }

  return PREMIERE_CLIP_IMPORT_EXTENSION_SET.has(normalizeExtension(ext));
};

/** `Clips.search` filter matching {@link PREMIERE_CLIP_IMPORT_EXTENSIONS}. */
export const getPremiereClipImportExtensionFilter = () => ({
  extension: { or: [...PREMIERE_CLIP_IMPORT_EXTENSIONS] },
});

/**
 * Clip types requested by every Premiere clip listing. `nonMedia` is listed explicitly:
 * the backend classifies `.mogrt` as `nonMedia` / `nonTranscodable` with only
 * `assets.original`, so it is dropped unless the search asks for that type.
 */
export const PREMIERE_LISTABLE_CLIP_TYPES: ClipType[] = [
  ClipType.video,
  ClipType.photo,
  ClipType.livePhoto,
  ClipType.animated,
  ClipType.audio,
  ClipType.nonMedia,
];

/**
 * Narrows a clip listing to {@link PREMIERE_CLIP_IMPORT_EXTENSIONS} so widening the
 * requested clip types doesn't surface non-importable `nonMedia` files.
 */
export const getPremiereClipListFilters = <TFilters extends object>(filters?: TFilters) => ({
  ...filters,
  ...getPremiereClipImportExtensionFilter(),
});

export const PREMIERE_UNSUPPORTED_IMPORT_MESSAGE =
  'No importable assets selected — some file types are not supported in Premiere';

/**
 * Where an installed `.mogrt` surfaces in Premiere. Named in success toasts because
 * templates never appear in the Project panel, so a plain "Imported" reads as a
 * silent failure. Premiere 25.0 replaced the Essential Graphics panel with
 * Graphics Templates, so don't reintroduce the old panel name here.
 */
export const PREMIERE_GRAPHICS_TEMPLATES_LOCATION = 'Graphics Templates > My Templates';

/** Resolved host filename (after sanitization), not clip.ext. */
export const isMogrtFileName = (fileName: string): boolean => fileName.trim().toLowerCase().endsWith('.mogrt');
